/**
 * Dashboard.jsx — v11 (Professional · Matches FolderDataTable aesthetic)
 * =======================================================================
 * Changes from previous:
 *  - Removed gradient module banner → replaced with flat bordered header row
 *  - Removed gradient avatars → plain initials in muted squared box
 *  - Removed hover card effects (lift, shadow, border color shifts)
 *  - Removed colored gradient user-card → flat bordered card
 *  - Unified font family: Segoe UI, Calibri, Helvetica Neue
 *  - Cards → flat bordered containers (no shadow, no rounded 12px)
 *  - Muted color palette: #1a1a1a, #555, #888, #d9d9d9, #f5f5f5
 *  - Border-radius reduced from 12→3-4px everywhere
 *  - Removed all hover/transition animations
 *  - Removed decorative gradient overlays / circle decorations
 *  - Tag styles match FolderDataTable (small, fontSize 10-11, border-radius 3)
 *  - Header height reduced, flatter, tighter
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Typography,
  Button,
  Menu,
  Tag,
  Dropdown,
  Breadcrumb,
  Divider,
  Badge,
  Tooltip,
  Drawer,
  List,
  Empty,
  Checkbox,
  message,
} from 'antd';
import {
  LogoutOutlined,
  FolderOutlined,
  FileAddOutlined,
  SettingOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  ShoppingCartOutlined,
  BankOutlined,
  ProfileOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileImageOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  TagOutlined,
  WarningOutlined,
  FireOutlined,
} from '@ant-design/icons';
import FolderSelector from './FolderSelector';
import FileUploader from './FileUploader';
import FolderDataTable from './FolderDataTable';
import ScanButton from './ScanButton';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

/* ═══════════════════════════════════════════
   SHARED STYLE TOKENS (match FolderDataTable)
   ═══════════════════════════════════════════ */
const FONT_FAMILY = "'Segoe UI', Calibri, 'Helvetica Neue', sans-serif";

const S = {
  page:          { fontFamily: FONT_FAMILY, fontSize: 13, color: '#1a1a1a' },
  cellText:      { fontSize: 12, color: '#1a1a1a' },
  cellMuted:     { fontSize: 11, color: '#999' },
  sectionTitle:  { fontSize: 12, fontWeight: 700, color: '#1a1a1a', letterSpacing: 0.3 },
  subLabel:      { fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 },
  panel:         { background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, marginBottom: 14 },
  panelHeader:   { padding: '8px 14px', borderBottom: '1px solid #e8e8e8', background: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 8 },
  panelBody:     { padding: '12px 14px' },
  plainBtn:      { borderRadius: 3, fontSize: 11 },
};

/* ─── Cookie / storage helpers ─── */
const COOKIE_KEY  = 'pts_recent_uploads';
const STORAGE_KEY = 'pts_recent_uploads';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const saveUploadsToCookie = (uploads) => {
  try {
    const data = JSON.stringify(uploads);
    const expires = new Date(Date.now() + ONE_WEEK_MS).toUTCString();
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(data)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {}
};
const loadUploadsFromCookie = () => {
  try {
    const match = document.cookie.split('; ').find(r => r.startsWith(`${COOKIE_KEY}=`));
    if (!match) return [];
    const data = JSON.parse(decodeURIComponent(match.split('=').slice(1).join('=')));
    if (!Array.isArray(data)) return [];
    const oneWeekAgo = Date.now() - ONE_WEEK_MS;
    return data.filter(i => (i.timestamp || 0) > oneWeekAgo);
  } catch { return []; }
};
const saveUploadsToStorage = (uploads) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(uploads)); } catch {}
  saveUploadsToCookie(uploads);
};
const loadUploadsFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      if (Array.isArray(data)) {
        const oneWeekAgo = Date.now() - ONE_WEEK_MS;
        const filtered = data.filter(i => (i.timestamp || 0) > oneWeekAgo);
        if (filtered.length !== data.length) saveUploadsToStorage(filtered);
        return filtered;
      }
    }
  } catch {}
  return loadUploadsFromCookie();
};
const clearExpiredUploads = (uploads) => {
  const oneWeekAgo = Date.now() - ONE_WEEK_MS;
  return uploads.filter(i => (i.timestamp || 0) > oneWeekAgo);
};

const getFileIcon = (filename) => {
  if (!filename) return <FileTextOutlined style={{ color: '#888' }} />;
  const ext = filename.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext))                       return <FilePdfOutlined   style={{ color: '#c41a1a' }} />;
  if (['xlsx', 'xls', 'csv'].includes(ext))        return <FileExcelOutlined style={{ color: '#1a7a1a' }} />;
  if (['doc', 'docx'].includes(ext))               return <FileWordOutlined  style={{ color: '#0958d9' }} />;
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <FileImageOutlined style={{ color: '#b38600' }} />;
  return <FileTextOutlined style={{ color: '#888' }} />;
};

const getRelativeTime = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7)  return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
};

/* ═══════════════════════════════════════════
   DASHBOARD COMPONENT
   ═══════════════════════════════════════════ */
const Dashboard = ({ username, role, onLogout }) => {
  const [collapsed, setCollapsed]         = useState(false);
  const [selectedMenu, setSelectedMenu]   = useState('site-petty-cash');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);
  const [dataTableRefreshKey, setDataTableRefreshKey] = useState(0);
  const [fuelModeEnabled, setFuelModeEnabled] = useState(false);

  useEffect(() => { setRecentUploads(loadUploadsFromStorage()); }, []);

  useEffect(() => {
    const i = setInterval(() => {
      setRecentUploads(prev => {
        const f = clearExpiredUploads(prev);
        if (f.length !== prev.length) saveUploadsToStorage(f);
        return f;
      });
    }, 30 * 60 * 1000);
    return () => clearInterval(i);
  }, []);

  const handleMenuClick = ({ key }) => {
    if (key === 'site-petty-cash' || key === 'procurement-pc') {
      setSelectedMenu(key);
      setSelectedFolder(null);
    }
  };

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    if (folder) message.success(`Selected Folder ${folder.name}`);
  };

  const handleUploadSuccess = useCallback((result) => {
    const newUpload = {
      id: Date.now() + Math.random(),
      filename: result.renamed_filename || result.key?.split('/').pop() || 'Unknown file',
      original_filename: result.original_filename || '',
      invoice_number: result.invoice_number || null,
      folder: selectedFolder?.name || '',
      module: selectedMenu === 'site-petty-cash' ? 'Site PC' : 'Procurement',
      path: result.key || '',
      time: new Date().toLocaleString(),
      timestamp: Date.now(),
    };
    setRecentUploads(prev => {
      const u = [newUpload, ...prev].slice(0, 50);
      saveUploadsToStorage(u);
      return u;
    });
    setDataTableRefreshKey(p => p + 1);
  }, [selectedFolder, selectedMenu]);

  const displayName = username.split('@')[0] || username;
  const initials    = displayName.substring(0, 2).toUpperCase();

  const moduleInfo = selectedMenu === 'site-petty-cash'
    ? { title: 'Site Petty Cash', range: '326 – 350', icon: <BankOutlined /> }
    : { title: 'Procurement PC',  range: '78 – 100',  icon: <ShoppingCartOutlined /> };

  const profileMenuItems = [
    {
      key: 'ph',
      label: (
        <div style={{ padding: '6px 0', fontFamily: FONT_FAMILY }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{displayName}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{username}</div>
          <div style={{ marginTop: 4 }}>
            <Tag style={{ fontSize: 10, borderRadius: 3, margin: 0, color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>
              {(role || 'user').replace('_', ' ').toUpperCase()}
            </Tag>
          </div>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    { key: 'profile',  icon: <ProfileOutlined />, label: 'My Profile' },
    { key: 'settings', icon: <SettingOutlined />, label: 'Settings'   },
    { type: 'divider' },
    { key: 'logout',   icon: <LogoutOutlined />,  label: 'Logout', danger: true },
  ];

  const handleProfileMenu = ({ key }) => { if (key === 'logout') onLogout(); };

  const sidebarItems = [
    {
      key: 'modules-group',
      type: 'group',
      label: !collapsed ? (
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: '#888' }}>
          MODULES
        </span>
      ) : null,
      children: [
        { key: 'site-petty-cash', icon: <BankOutlined         style={{ fontSize: 15 }} />, label: 'Site Petty Cash' },
        { key: 'procurement-pc',  icon: <ShoppingCartOutlined style={{ fontSize: 15 }} />, label: 'Procurement PC'  },
      ],
    },
  ];

  /* ═══════════════════════════════════════════ RENDER ═══════════════════════════════════════════ */
  return (
    <Layout style={{ minHeight: '100vh', fontFamily: FONT_FAMILY }}>

      {/* ════ SIDEBAR ════ */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={240}
        collapsedWidth={64}
        style={{
          background: '#fafafa',
          borderRight: '1px solid #d9d9d9',
          position: 'fixed',
          left: 0, top: 0, bottom: 0,
          zIndex: 10,
          overflow: 'auto',
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: collapsed ? '16px 0' : '18px 18px 14px',
            borderBottom: '1px solid #d9d9d9',
            textAlign: collapsed ? 'center' : 'left',
            background: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <img
              src="/tab.png"
              alt="PTS"
              style={{ width: collapsed ? 30 : 36, height: collapsed ? 30 : 36, objectFit: 'contain', flexShrink: 0 }}
            />
            {!collapsed && (
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a', letterSpacing: 0.3 }}>PHOENICIAN</div>
                <div style={{ fontSize: 9, color: '#888', letterSpacing: 1.4, fontWeight: 600 }}>TECHNICAL SERVICES LLC</div>
              </div>
            )}
          </div>
        </div>

        {/* Menu */}
        <Menu
          mode="inline"
          selectedKeys={[selectedMenu]}
          onClick={handleMenuClick}
          items={sidebarItems}
          style={{
            border: 'none',
            marginTop: 8,
            padding: '0 6px',
            background: 'transparent',
            fontFamily: FONT_FAMILY,
            fontSize: 12,
          }}
        />

        {/* User card — expanded */}
        {!collapsed && (
          <div
            style={{
              position: 'absolute',
              bottom: 14, left: 12, right: 12,
              background: '#fff',
              border: '1px solid #d9d9d9',
              borderRadius: 3,
              padding: '10px 12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 32, height: 32, borderRadius: 3,
                  background: '#1a5276', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div
                  style={{
                    fontSize: 12, fontWeight: 600, color: '#1a1a1a',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {displayName}
                </div>
                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 }}>
                  {(role || 'user').replace('_', ' ')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User avatar — collapsed */}
        {collapsed && (
          <div style={{ position: 'absolute', bottom: 14, left: 0, right: 0, textAlign: 'center' }}>
            <Tooltip title={displayName} placement="right">
              <div
                style={{
                  width: 30, height: 30, borderRadius: 3,
                  background: '#1a5276', color: '#fff',
                  fontSize: 11, fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                {initials}
              </div>
            </Tooltip>
          </div>
        )}
      </Sider>

      {/* ════ MAIN AREA ════ */}
      <Layout
        style={{
          marginLeft: collapsed ? 64 : 240,
          transition: 'margin-left 0.2s',
          background: '#f5f5f5',
        }}
      >
        {/* ── Top Header ── */}
        <Header
          style={{
            background: '#fff',
            padding: '0 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #d9d9d9',
            position: 'sticky', top: 0, zIndex: 9,
            height: 48,
            lineHeight: 'normal',
            fontFamily: FONT_FAMILY,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 14, width: 30, height: 30, color: '#555' }}
              size="small"
            />
            <Breadcrumb
              items={[
                { title: <span style={{ color: '#888', fontSize: 12 }}><HomeOutlined /> Home</span> },
                { title: <span style={{ color: '#888', fontSize: 12 }}>Petty Cash</span> },
                { title: <span style={{ color: '#1a5276', fontWeight: 600, fontSize: 12 }}>{moduleInfo.title}</span> },
              ]}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Tooltip title="Recent Uploads">
              <Badge count={recentUploads.length} size="small" offset={[-2, 2]}>
                <Button
                  type="text"
                  icon={<BellOutlined style={{ fontSize: 15, color: '#555' }} />}
                  style={{ width: 32, height: 32, borderRadius: 3 }}
                  onClick={() => setNotificationOpen(true)}
                  size="small"
                />
              </Badge>
            </Tooltip>

            <Divider type="vertical" style={{ height: 22, margin: '0 8px', borderColor: '#d9d9d9' }} />

            <Dropdown
              menu={{ items: profileMenuItems, onClick: handleProfileMenu }}
              trigger={['click']}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  cursor: 'pointer', padding: '3px 8px 3px 3px',
                  borderRadius: 3,
                  border: '1px solid transparent',
                }}
              >
                <div
                  style={{
                    width: 28, height: 28, borderRadius: 3,
                    background: '#1a5276', color: '#fff',
                    fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {initials}
                </div>
                <div style={{ lineHeight: 1.2 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{displayName}</div>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {(role || 'user').replace('_', ' ')}
                  </div>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* ── Page Content ── */}
        <Content style={{ padding: 18, ...S.page }}>

          {/* Flat Module Header Row (replaces gradient banner) */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #d9d9d9',
              borderRadius: 3,
              padding: '12px 16px',
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 34, height: 34, borderRadius: 3,
                background: '#f5f5f5',
                border: '1px solid #d9d9d9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#1a5276', fontSize: 16, flexShrink: 0,
              }}
            >
              {moduleInfo.icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
                {moduleInfo.title}
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                Folders {moduleInfo.range} · Upload &amp; manage petty cash documents
              </div>
            </div>
            <Tag style={{ fontSize: 10, borderRadius: 3, margin: 0, color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>
              {selectedMenu === 'site-petty-cash' ? 'SITE PC' : 'PROCUREMENT'}
            </Tag>
          </div>

          {/* Folder Selector panel */}
          <div style={S.panel}>
            <div style={S.panelHeader}>
              <FolderOutlined style={{ color: '#1a5276', fontSize: 13 }} />
              <span style={S.sectionTitle}>Select Folder</span>
            </div>
            <div style={S.panelBody}>
              <FolderSelector moduleType={selectedMenu} onFolderSelect={handleFolderSelect} />

              {/* Fuel Mode */}
              <div
                style={{
                  marginTop: 12,
                  padding: '8px 10px',
                  background: '#fafafa',
                  border: '1px solid #e8e8e8',
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Checkbox
                  checked={fuelModeEnabled}
                  onChange={e => setFuelModeEnabled(e.target.checked)}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>
                    <FireOutlined style={{ color: '#d46b08', marginRight: 4 }} />
                    Fuel Invoices
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
                    Enable to assign fuel names (driver/vehicle) to scanned invoices. Filename will include serial number.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upload panel */}
          {selectedFolder && (
            <div style={S.panel}>
              <div style={{ ...S.panelHeader, flexWrap: 'wrap' }}>
                <FileAddOutlined style={{ color: '#1a7a1a', fontSize: 13 }} />
                <span style={S.sectionTitle}>Upload Documents</span>
                <Tag style={{ fontSize: 10, borderRadius: 3, margin: '0 0 0 4px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>
                  Folder {selectedFolder.name}
                </Tag>
                <div style={{ marginLeft: 'auto' }}>
                  <ScanButton
                    folderPath={selectedFolder.path}
                    folderNumber={selectedFolder.name}
                    moduleType={selectedMenu}
                    folderName={selectedFolder.name}
                    username={username}
                    onProcessComplete={() => setDataTableRefreshKey(p => p + 1)}
                    isFuelMode={fuelModeEnabled}
                  />
                </div>
              </div>
              <div style={S.panelBody}>
                <FileUploader
                  folderPath={selectedFolder.path}
                  folderName={selectedFolder.name}
                  onUploadSuccess={handleUploadSuccess}
                />
              </div>
            </div>
          )}

          {/* FolderDataTable */}
          {selectedFolder && (
            <div style={{ ...S.panel, padding: '14px' }}>
              <FolderDataTable
                key={`${selectedMenu}-${selectedFolder.name}-${dataTableRefreshKey}`}
                folderNumber={selectedFolder.name}
                moduleType={selectedMenu}
                folderName={selectedFolder.name}
                username={username}
              />
            </div>
          )}
        </Content>

        {/* Footer */}
        <div
          style={{
            textAlign: 'center',
            padding: '12px 18px 16px',
            color: '#888',
            fontSize: 11,
            fontWeight: 500,
            fontFamily: FONT_FAMILY,
          }}
        >
          © 2026 Phoenician Technical Services LLC · All rights reserved
        </div>
      </Layout>

      {/* ════ NOTIFICATIONS DRAWER ════ */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_FAMILY }}>
            <BellOutlined style={{ color: '#1a5276' }} />
            <span style={{ fontWeight: 700, color: '#1a1a1a', fontSize: 13 }}>Recent Uploads</span>
            <Badge
              count={recentUploads.length}
              style={{ backgroundColor: '#1a5276', marginLeft: 4, fontSize: 10 }}
              size="small"
            />
          </div>
        }
        placement="right"
        onClose={() => setNotificationOpen(false)}
        open={notificationOpen}
        width={380}
        styles={{ body: { padding: 0, fontFamily: FONT_FAMILY } }}
      >
        <div
          style={{
            padding: '8px 16px',
            background: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <ClockCircleOutlined style={{ color: '#1a5276', fontSize: 11 }} />
          <Text style={{ fontSize: 11, color: '#888' }}>
            History saved; auto-clears after 7 days
          </Text>
        </div>

        {recentUploads.length === 0 ? (
          <div style={{ padding: '50px 20px', textAlign: 'center' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text style={{ fontSize: 13, color: '#555' }}>No uploads yet</Text>
                  <br />
                  <Text style={{ fontSize: 11, color: '#888' }}>Uploaded documents appear here</Text>
                </div>
              }
            />
          </div>
        ) : (
          <List
            dataSource={recentUploads}
            renderItem={(item) => (
              <List.Item
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 32, height: 32, borderRadius: 3,
                      background: '#fafafa',
                      border: '1px solid #e8e8e8',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, flexShrink: 0,
                    }}
                  >
                    {getFileIcon(item.filename)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12, fontWeight: 600, color: '#1a1a1a',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                    >
                      {item.filename}
                    </div>

                    {item.original_filename && item.original_filename !== item.filename && (
                      <div
                        style={{
                          fontSize: 10, color: '#888',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          marginTop: 2,
                        }}
                      >
                        Was: {item.original_filename}
                      </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                      <Tag style={{ fontSize: 9, lineHeight: '16px', borderRadius: 3, margin: 0, padding: '0 5px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>
                        {item.module}
                      </Tag>
                      <Tag style={{ fontSize: 9, lineHeight: '16px', borderRadius: 3, margin: 0, padding: '0 5px', color: '#555', background: '#f0f0f0', border: '1px solid #d9d9d9' }}>
                        Folder {item.folder}
                      </Tag>

                      {item.invoice_number ? (
                        <Tag
                          icon={<TagOutlined style={{ fontSize: 9 }} />}
                          style={{ fontSize: 9, lineHeight: '16px', borderRadius: 3, margin: 0, padding: '0 5px', color: '#237804', background: '#f6ffed', border: '1px solid #b7eb8f' }}
                        >
                          {item.invoice_number}
                        </Tag>
                      ) : (
                        item.filename?.startsWith('detection_') && (
                          <Tag
                            icon={<WarningOutlined style={{ fontSize: 9 }} />}
                            style={{ fontSize: 9, lineHeight: '16px', borderRadius: 3, margin: 0, padding: '0 5px', color: '#d46b08', background: '#fffbe6', border: '1px solid #ffe58f' }}
                          >
                            No invoice
                          </Tag>
                        )
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5 }}>
                      <CheckCircleOutlined style={{ fontSize: 10, color: '#52c41a' }} />
                      <Text style={{ fontSize: 10, color: '#888' }}>Uploaded</Text>
                      <Text style={{ fontSize: 10, color: '#aaa', marginLeft: 3 }}>
                        · {item.timestamp ? getRelativeTime(item.timestamp) : item.time}
                      </Text>
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </Drawer>

      {/* Global CSS overrides to kill hover/shadow/gradient AntD defaults */}
      <style>{`
        body, .ant-layout { font-family: ${FONT_FAMILY} !important; }

        /* Sidebar menu — flat, no hover lift, no rounded bg */
        .ant-menu-light.ant-menu-inline .ant-menu-item {
          border-radius: 3px !important;
          height: 32px !important;
          line-height: 32px !important;
          margin: 2px 0 !important;
          font-size: 12px !important;
          color: #1a1a1a !important;
        }
        .ant-menu-light.ant-menu-inline .ant-menu-item:hover {
          background: #f0f0f0 !important;
          color: #1a5276 !important;
        }
        .ant-menu-light.ant-menu-inline .ant-menu-item-selected {
          background: #e8f4fd !important;
          color: #1a5276 !important;
          font-weight: 600 !important;
        }
        .ant-menu-light.ant-menu-inline .ant-menu-item-selected::after {
          border-right-color: #1a5276 !important;
        }

        /* Buttons — no shadow */
        .ant-btn { box-shadow: none !important; font-family: ${FONT_FAMILY} !important; }

        /* Breadcrumb */
        .ant-breadcrumb ol { align-items: center; }

        /* Drawer title */
        .ant-drawer-header { padding: 12px 16px !important; border-bottom: 1px solid #d9d9d9 !important; }
        .ant-drawer-title { font-family: ${FONT_FAMILY} !important; }

        /* List no hover background */
        .ant-list-item:hover { background: #fafafa !important; }
      `}</style>
    </Layout>
  );
};

export default Dashboard;